import React, { useState, useEffect, useRef } from 'react';
import { X, Send, UserPlus, AlertCircle } from 'lucide-react';
import api from '../api';
import { NoAutoFillInput } from './ui/NoAutoFillInput';

// eslint-disable-next-line no-unused-vars
const StrangerChatModal = ({ isOpen, onClose, targetUserId, targetUserName, targetAvatar, onAddFriend, _t }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [roundCount, setRoundCount] = useState(0);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [hasRequestedFriend, setHasRequestedFriend] = useState(false);
  const messagesEndRef = useRef(null);

  const MAX_ROUNDS = 10;

  useEffect(() => {
    if (isOpen) {
      setMessages([
        {
          id: 1,
          sender: 'ai',
          text: `你好，我是${targetUserName}的数字分身。我们可以进行最多${MAX_ROUNDS}轮对话。`,
          timestamp: Date.now()
        }
      ]);
      setRoundCount(0);
      setShowAddFriend(false);
      setHasRequestedFriend(false);
    }
  }, [isOpen, targetUserName]);

  useEffect(() => {
    return () => {
      if (roundCount >= MAX_ROUNDS && !hasRequestedFriend && onClose) {
        const event = new CustomEvent('strangerChatEndedWithoutFriendRequest', {
          detail: {
            targetUserId,
            targetUserName,
            chatLog: messages.filter(m => m.sender !== 'system'),
            roundCount
          }
        });
        window.dispatchEvent(event);
      }
    };
  }, [roundCount, hasRequestedFriend, targetUserId, targetUserName, messages, onClose]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || roundCount >= MAX_ROUNDS) return;

    const userMsg = {
      id: Date.now(),
      sender: 'me',
      text: inputValue.trim(),
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');

    const newRoundCount = roundCount + 1;
    setRoundCount(newRoundCount);

    try {
      const aiResponse = await api.getAIReply(targetUserId, 'stranger', userMsg.text, targetUserName);
      const aiReplyText = typeof aiResponse === 'string' ? aiResponse : aiResponse.reply;
      
      const aiMsg = {
        id: Date.now() + 1,
        sender: 'ai',
        text: aiReplyText,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, aiMsg]);

      if (newRoundCount >= MAX_ROUNDS) {
        setTimeout(() => {
          setShowAddFriend(true);
          const limitMsg = {
            id: Date.now() + 2,
            sender: 'system',
            text: `对话轮数已达上限（${MAX_ROUNDS}/${MAX_ROUNDS}）。如需继续对话，请添加对方为好友。`,
            timestamp: Date.now()
          };
          setMessages(prev => [...prev, limitMsg]);
        }, 500);
      }
    } catch (error) {
      console.error('获取AI回复失败:', error);
      const errorMsg = {
        id: Date.now() + 1,
        sender: 'system',
        text: 'AI回复失败，请稍后再试。',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMsg]);
    }
  };

  const handleAddFriend = () => {
    setHasRequestedFriend(true);
    if (onAddFriend) {
      onAddFriend(targetUserId, targetUserName);
    }
    const requestMsg = {
      id: Date.now(),
      sender: 'system',
      text: '已发送好友申请，等待对方确认...',
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, requestMsg]);
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-[100] bg-[#050510] flex flex-col page-enter">
      <div className="h-20 flex items-center justify-between px-6 glass-card border-b border-white/5">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X size={24} className="text-white" />
          </button>
          <div className="flex items-center gap-3">
            <img src={targetAvatar} alt={targetUserName} className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--neon-primary)] to-blue-600" />
            <div>
              <h3 className="text-white font-bold">{targetUserName}的分身</h3>
              <p className="text-xs text-[var(--neon-primary)]">
                {roundCount < MAX_ROUNDS ? `剩余${MAX_ROUNDS - roundCount}轮对话` : '对话已结束'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-40">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
            {msg.sender === 'ai' && (
              <img src={targetAvatar} alt="AI" className="w-8 h-8 rounded-full mr-2 bg-gradient-to-br from-[var(--neon-primary)] to-blue-600" />
            )}
            <div className={`max-w-[75%] px-4 py-3 rounded-2xl ${
              msg.sender === 'me' 
                ? 'bg-[var(--neon-primary)] text-black rounded-br-none' 
                : msg.sender === 'system'
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                : 'glass-card text-white rounded-bl-none'
            }`}>
              {msg.sender === 'system' && <AlertCircle size={16} className="inline mr-1 mb-0.5" />}
              <p className="text-sm">{msg.text}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="absolute bottom-0 left-0 w-full px-4 pb-6 pt-4 bg-gradient-to-t from-[#050510] via-[#050510]/95 to-transparent safe-bottom">
        {showAddFriend ? (
          <div className="space-y-3">
            {!hasRequestedFriend ? (
              <button
                onClick={handleAddFriend}
                className="w-full py-4 bg-[var(--neon-primary)] text-black rounded-2xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              >
                <UserPlus size={20} />
                添加对方为好友
              </button>
            ) : (
              <div className="w-full py-4 bg-white/10 text-white rounded-2xl font-bold text-center">
                已发送好友申请
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full py-3 bg-white/5 text-gray-400 rounded-2xl font-medium hover:bg-white/10 transition-colors"
            >
              结束对话
            </button>
          </div>
        ) : (
          <div className="glass-card rounded-[32px] p-2 flex items-center gap-2">
            <NoAutoFillInput
              type="text"
              inputMode="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={roundCount < MAX_ROUNDS ? "输入消息..." : "对话已结束"}
              disabled={roundCount >= MAX_ROUNDS}
              name="stranger-message"
              id="stranger-message"
              className="flex-1 bg-transparent border-none outline-none text-white py-3 px-4 disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || roundCount >= MAX_ROUNDS}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                inputValue.trim() && roundCount < MAX_ROUNDS
                  ? 'bg-[var(--neon-primary)] text-black'
                  : 'bg-white/10 text-gray-500'
              }`}
            >
              <Send size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrangerChatModal;
